import axios, { type AxiosResponse } from 'axios';
import { isMobileUA } from '@/utils/platform';

const baseURL = import.meta.env.VITE_API_BASE || '/api';

interface ApiRes<T = unknown> { code: number; msg: string; data: T; }

const http = axios.create({ baseURL, withCredentials: true, timeout: 15000 });

function extractData<T>(res: AxiosResponse<ApiRes<T>>): T { return res.data.data; }

http.interceptors.response.use(
  (res) => {
    const { code, msg } = res.data;
    if (code !== 0) {
      const errMsg = msg || '操作失败';
      if (isMobileUA()) { import('vant').then(({ showToast }) => showToast(errMsg)).catch(() => {}); }
      else { import('element-plus').then(({ ElMessage }) => ElMessage.error(errMsg)).catch(() => {}); }
      return Promise.reject(new Error(errMsg));
    }
    return res;
  },
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || '';
    if (status === 401) {
      const path = window.location.pathname;
      const isH5 = path.startsWith('/h5');
      const isLoginPage = path === '/pc/login' || path === '/m/login';
      if (isH5) {
        const errMsg = err.response?.data?.msg || '访问已过期，请重新扫码进入';
        if (path.startsWith('/h5/customer/orders')) {
          localStorage.removeItem('syhq_user');
          import('vant').then(({ showToast }) => showToast(errMsg)).catch(() => {});
          window.location.href = `/h5/customer/login?redirect=${encodeURIComponent(path + window.location.search)}`;
        } else {
          import('vant').then(({ showToast }) => showToast(errMsg)).catch(() => {});
        }
      } else if (!isLoginPage) {
        localStorage.removeItem('syhq_user');
        const loginPath = path.startsWith('/m/') ? '/m/login' : '/pc/login';
        window.location.href = `${loginPath}?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      }
      return Promise.reject(err);
    }
    const errMsg = err.response?.data?.msg || err.message || '网络错误';
    if (isMobileUA()) { import('vant').then(({ showToast }) => showToast(errMsg)).catch(() => {}); }
    else { import('element-plus').then(({ ElMessage }) => ElMessage.error(errMsg)).catch(() => {}); }
    return Promise.reject(err);
  },
);

export function get<T>(url: string, params?: Record<string, unknown>): Promise<T> { return http.get<ApiRes<T>>(url, { params }).then(extractData); }
export function post<T>(url: string, data?: unknown): Promise<T> { return http.post<ApiRes<T>>(url, data).then(extractData); }
export function put<T>(url: string, data?: unknown): Promise<T> { return http.put<ApiRes<T>>(url, data).then(extractData); }
export function del<T>(url: string, params?: Record<string, unknown>): Promise<T> { return http.delete<ApiRes<T>>(url, { params }).then(extractData); }
export function page<T>(url: string, params?: Record<string, unknown>): Promise<{ list: T[]; total: number }> { return http.get<ApiRes<{ list: T[]; total: number }>>(url, { params }).then(extractData); }
export default http;